import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  api,
  type NewsJobMode,
  type NewsJobResponse,
  type NewsReadingProfile,
} from "../lib/api";
import { resolveNewsGoogleReconsentCompleteUrl } from "../lib/config";
import { logError, toUserMessage } from "../lib/errors";
import { normalizeGoogleIntegrationStatus, type GoogleIntegrationView } from "../lib/googleIntegration";
import { GOOGLE_SCOPE_BUNDLES } from "../lib/googleScopes";
import { normalizeNewsGoogleAuthorizationUrl, writePendingNewsGoogleReconsentSnapshot } from "../lib/newsGoogleReconsent";
import {
  createPendingNewsJobSnapshot,
  type PendingNewsJobSnapshot,
  writePendingNewsJobSnapshot,
} from "../lib/newsJob";
import { NEWS_GMAIL_SEND_SCOPE_BUNDLE, resolveNewsJobConflictDisplay } from "../lib/newsJobError";
import {
  readLineLinkSnapshot,
  resolveLineLinkSnapshotFromApiStatus,
  type LineLinkSnapshot,
  writeLineLinkSnapshot,
} from "../lib/lineLink";
import { storage } from "../lib/storage";
import { UI_TEXT_TONE } from "../lib/uiTone";

type RequestRouteState = {
  returnTo?: string;
  requestForm?: Partial<RequestPageFormState>;
};

type RequestPageFormState = {
  keyword: string;
  notificationType: "line" | "email";
  email: string;
  readingProfile: NewsReadingProfile;
  includeAudio: boolean;
};

type InlineError = {
  message: string;
  detail?: string;
  newsGoogleReconsent?: {
    authorizationUrl: string;
    requiredScopeBundle: string;
  };
};

type SubmitResult = {
  response: NewsJobResponse;
  pendingSnapshot: PendingNewsJobSnapshot;
};

const INITIAL_FORM: RequestPageFormState = {
  keyword: "",
  notificationType: "line",
  email: "",
  readingProfile: "think",
  includeAudio: false,
};

function createLoadingGoogleIntegrationView(): GoogleIntegrationView {
  return {
    state: "loading",
    scopeBundleGranted: false,
    missingScopes: [],
  };
}

function createErrorGoogleIntegrationView(): GoogleIntegrationView {
  return {
    state: "error",
    scopeBundleGranted: false,
    missingScopes: [],
  };
}

type NewsJobRequestPageProps = {
  mode: NewsJobMode;
};

export default function NewsJobRequestPage({ mode }: NewsJobRequestPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = (typeof location.state === "object" && location.state ? location.state : {}) as RequestRouteState;
  const returnTo = String(routeState.returnTo || "/request");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lineLinkSnapshot, setLineLinkSnapshot] = useState<LineLinkSnapshot>(readLineLinkSnapshot());
  const [googleStatus, setGoogleStatus] = useState<GoogleIntegrationView>(createLoadingGoogleIntegrationView());
  const [form, setForm] = useState<RequestPageFormState>({
    ...INITIAL_FORM,
    email: storage.getNewsEmail() ?? storage.getGoogleSso()?.email ?? "",
  });
  const [error, setError] = useState<InlineError | null>(null);
  const [result, setResult] = useState<SubmitResult | null>(null);

  const canUseLine = lineLinkSnapshot.status === "linked";
  const canUseNewsEmail = googleStatus.state === "connected";
  const title = mode === "report" ? "レポートを送ってもらう" : "ニュースを送ってもらう";
  const keywordLabel = mode === "report" ? "調査テーマ" : "キーワード";
  const keywordPlaceholder =
    mode === "report" ? "例: 生成AIの最新動向と市場展望" : "例: AI、生成AI、国内ニュース";

  useEffect(() => {
    const nextForm = routeState.requestForm;
    if (!nextForm) return;

    setForm((prev) => ({
      keyword: typeof nextForm.keyword === "string" ? nextForm.keyword : prev.keyword,
      notificationType:
        nextForm.notificationType === "email" || nextForm.notificationType === "line"
          ? nextForm.notificationType
          : prev.notificationType,
      email: typeof nextForm.email === "string" ? nextForm.email : prev.email,
      readingProfile:
        nextForm.readingProfile === "grasp" ||
        nextForm.readingProfile === "organize" ||
        nextForm.readingProfile === "think"
          ? nextForm.readingProfile
          : prev.readingProfile,
      includeAudio: typeof nextForm.includeAudio === "boolean" ? nextForm.includeAudio : prev.includeAudio,
    }));
  }, [routeState.requestForm]);

  useEffect(() => {
    let active = true;

    (async () => {
      setLoading(true);
      try {
        const [lineStatus, googleIntegrationStatus] = await Promise.all([
          api.getLineLinkStatus(),
          api.getGoogleIntegrationStatus(NEWS_GMAIL_SEND_SCOPE_BUNDLE),
        ]);

        if (!active) return;

        const nextLineSnapshot = resolveLineLinkSnapshotFromApiStatus(lineStatus, readLineLinkSnapshot());
        writeLineLinkSnapshot(nextLineSnapshot);
        setLineLinkSnapshot(nextLineSnapshot);
        setGoogleStatus(normalizeGoogleIntegrationStatus(googleIntegrationStatus));
        setForm((prev) => ({
          ...prev,
          notificationType: nextLineSnapshot.status === "linked" ? prev.notificationType : "email",
        }));
      } catch (refreshError) {
        logError(refreshError);
        if (!active) return;
        setLineLinkSnapshot(readLineLinkSnapshot());
        setGoogleStatus(createErrorGoogleIntegrationView());
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!result) return;
    const timer = window.setTimeout(() => {
      setResult(null);
    }, 4000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [result]);

  const requestState = useMemo(
    () => ({
      requestForm: form,
      returnTo: location.pathname,
    }),
    [form, location.pathname]
  );

  const closePage = () => {
    navigate(returnTo, { replace: true });
  };

  const openLineLink = () => {
    navigate("/line-link", {
      state: {
        returnTo: location.pathname,
        returnState: requestState,
      },
    });
  };

  const openGoogleLink = () => {
    navigate(
      `/google-link?returnTo=${encodeURIComponent(location.pathname)}&mode=single&scopeBundle=${encodeURIComponent(
        GOOGLE_SCOPE_BUNDLES.bundle
      )}`,
      {
        state: {
          returnTo: location.pathname,
          mode: "single",
          scopeBundle: GOOGLE_SCOPE_BUNDLES.bundle,
          returnState: requestState,
        },
      }
    );
  };

  const startNewsGoogleReconsent = () => {
    const reconsent = error?.newsGoogleReconsent;
    if (!reconsent) return;

    const keyword = form.keyword.trim();
    const email = form.email.trim();
    const wizmateUserId = storage.getAuth().userId?.trim() ?? "";
    const redirectUrl = resolveNewsGoogleReconsentCompleteUrl();
    const authorizationUrl = normalizeNewsGoogleAuthorizationUrl(reconsent.authorizationUrl);

    if (!keyword || !email || !wizmateUserId || !authorizationUrl) {
      setError({
        message: "Google連携の再開に必要な情報を確認できませんでした。もう一度お試しください。",
      });
      return;
    }

    writePendingNewsGoogleReconsentSnapshot({
      keyword,
      notificationType: "email",
      email,
      wizmateUserId,
      scopeBundle: reconsent.requiredScopeBundle ?? GOOGLE_SCOPE_BUNDLES.bundle,
      authorizationUrl,
      redirectUrl,
      createdAt: new Date().toISOString(),
      mode,
      readingProfile: mode === "news" ? form.readingProfile : undefined,
      deliveryOption: form.includeAudio ? "text_and_audio" : "text",
    });

    window.location.assign(authorizationUrl);
  };

  const handleSubmit = async () => {
    if (submitting) return;

    const keyword = form.keyword.trim();
    if (!keyword) {
      setError({ message: `${keywordLabel}を入力してください。` });
      return;
    }

    if (form.notificationType === "line" && !canUseLine) {
      setError({ message: "LINE連携を完了してからお試しください。" });
      return;
    }

    const email = form.email.trim();
    if (form.notificationType === "email" && !email) {
      setError({ message: "送信先メールアドレスを入力してください。" });
      return;
    }

    if (form.notificationType === "email" && !canUseNewsEmail) {
      setError({ message: "メール送信には Google 連携が必要です。" });
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await api.createNewsJob({
        keyword,
        notificationType: form.notificationType,
        ...(form.notificationType === "email"
          ? {
              email,
              redirectUrl: resolveNewsGoogleReconsentCompleteUrl(),
            }
          : {}),
        mode,
        ...(mode === "news" ? { readingProfile: form.readingProfile } : {}),
        deliveryOption: form.notificationType === "line" ? "text" : form.includeAudio ? "text_and_audio" : "text",
      });

      if (form.notificationType === "email" && email) {
        storage.setNewsEmail(email);
      }

      const pendingSnapshot: PendingNewsJobSnapshot = {
        ...createPendingNewsJobSnapshot(response),
        mode,
        readingProfile: mode === "news" ? form.readingProfile : null,
        deliveryOption: form.notificationType === "line" ? "text" : form.includeAudio ? "text_and_audio" : "text",
      };
      writePendingNewsJobSnapshot(pendingSnapshot);
      setResult({ response, pendingSnapshot });
    } catch (submitError) {
      logError(submitError);
      const conflictDisplay = resolveNewsJobConflictDisplay(submitError);
      if (
        form.notificationType === "email" &&
        conflictDisplay?.shouldShowGoogleReconnect &&
        conflictDisplay.connectAuthorizationUrl
      ) {
        const authorizationUrl = normalizeNewsGoogleAuthorizationUrl(conflictDisplay.connectAuthorizationUrl);
        if (!authorizationUrl) {
          setError({ message: "Google連携URLを確認できませんでした。しばらくしてからお試しください。" });
        } else {
          setError({
            message: "Gmail 送信には Google 連携の再確認が必要です。",
            detail: conflictDisplay.detail,
            newsGoogleReconsent: {
              authorizationUrl,
              requiredScopeBundle: conflictDisplay.requiredScopeBundle ?? GOOGLE_SCOPE_BUNDLES.bundle,
            },
          });
        }
      } else {
        setError({
          message: conflictDisplay?.message ?? toUserMessage(submitError),
          detail: conflictDisplay?.detail,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-xl items-center justify-center">
        <div className="relative w-full max-w-full rounded-[2.4rem] border border-white/80 bg-white/90 p-4 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <button
            type="button"
            onClick={closePage}
            aria-label="閉じる"
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#EADBC8] bg-[#FFF8F4] text-[#7C5A4E] transition hover:bg-white"
          >
            ×
          </button>

          <div className="mb-5 pr-12">
            <h1 className={`text-2xl font-black tracking-tight ${UI_TEXT_TONE.title}`}>{title}</h1>
          </div>

          {loading ? <p className="text-sm text-[#7B6861]">読み込み中です...</p> : null}

          {!loading ? (
            <>
              {!canUseLine || !canUseNewsEmail ? (
                <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
                  {!canUseLine ? (
                    <button
                      type="button"
                      onClick={openLineLink}
                      disabled={submitting}
                      className="inline-flex items-center justify-center rounded-full bg-[#5F8B57] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#4E7348] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      LINEと連携する
                    </button>
                  ) : null}
                  {!canUseNewsEmail ? (
                    <button
                      type="button"
                      onClick={openGoogleLink}
                      disabled={submitting}
                      className="inline-flex items-center justify-center rounded-full bg-[#B07A55] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#986648] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Googleと連携する
                    </button>
                  ) : null}
                </div>
              ) : null}

              <div className="rounded-[1.5rem] border border-[#E4D5C8] bg-white px-5 py-5">
                <label className="block">
                  <span className="mb-2 block text-sm font-bold text-[#5D4D46]">{keywordLabel}</span>
                  <input
                    type="text"
                    value={form.keyword}
                    onChange={(event) => {
                      setForm((prev) => ({ ...prev, keyword: event.target.value }));
                      setError(null);
                    }}
                    placeholder={keywordPlaceholder}
                    className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                    disabled={submitting}
                    maxLength={100}
                  />
                </label>

                {mode === "news" ? (
                  <fieldset className="mt-4 block">
                    <legend className="mb-2 block text-sm font-bold text-[#5D4D46]">内容のボリューム</legend>
                    <div className="grid grid-cols-3 gap-2">
                      {(
                        [
                          { value: "grasp", label: "さっと把握" },
                          { value: "organize", label: "要点を整理" },
                          { value: "think", label: "しっかり読む" },
                        ] as const
                      ).map(({ value, label }) => (
                        <label
                          key={value}
                          className={`flex cursor-pointer items-center justify-center rounded-2xl border px-3 py-2 text-sm font-bold shadow-sm transition-colors ${
                            form.readingProfile === value
                              ? "border-[#D1907B] bg-[#FFF7F1] text-[#7C5A4E]"
                              : "border-[#EADBC8] bg-white text-[#5D4D46]"
                          } ${submitting ? "cursor-not-allowed opacity-60" : ""}`}
                        >
                          <input
                            type="radio"
                            name={`readingProfile-${mode}`}
                            value={value}
                            checked={form.readingProfile === value}
                            onChange={() => {
                              setForm((prev) => ({ ...prev, readingProfile: value }));
                              setError(null);
                            }}
                            disabled={submitting}
                            className="sr-only"
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                ) : null}

                <fieldset className="mt-4 block">
                  <legend className="mb-2 block text-sm font-bold text-[#5D4D46]">通知先</legend>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label
                      className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm shadow-sm transition-colors ${
                        form.notificationType === "line"
                          ? "border-[#D1907B] bg-[#FFF7F1] text-[#7C5A4E]"
                          : "border-[#EADBC8] bg-white text-[#5D4D46]"
                      } ${canUseLine && !submitting ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
                    >
                      <input
                        type="radio"
                        name={`notificationType-${mode}`}
                        value="line"
                        checked={form.notificationType === "line"}
                        onChange={() => {
                          setForm((prev) => ({ ...prev, notificationType: "line", includeAudio: false }));
                          setError(null);
                        }}
                        disabled={submitting || !canUseLine}
                        className="h-4 w-4 accent-[#A67C52]"
                      />
                      <span className="font-bold">LINE</span>
                    </label>

                    <label
                      className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm shadow-sm transition-colors ${
                        form.notificationType === "email"
                          ? "border-[#D1907B] bg-[#FFF7F1] text-[#7C5A4E]"
                          : "border-[#EADBC8] bg-white text-[#5D4D46]"
                      } ${submitting || !canUseNewsEmail ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                    >
                      <input
                        type="radio"
                        name={`notificationType-${mode}`}
                        value="email"
                        checked={form.notificationType === "email"}
                        onChange={() => {
                          setForm((prev) => ({ ...prev, notificationType: "email" }));
                          setError(null);
                        }}
                        disabled={submitting || !canUseNewsEmail}
                        className="h-4 w-4 accent-[#A67C52]"
                      />
                      <span className="font-bold">メール</span>
                    </label>
                  </div>
                </fieldset>

                {form.notificationType === "email" ? (
                  <label className="mt-4 block">
                    <span className="mb-2 block text-sm font-bold text-[#5D4D46]">送信先メールアドレス</span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(event) => {
                        setForm((prev) => ({ ...prev, email: event.target.value }));
                        setError(null);
                      }}
                      placeholder="例: your@example.com"
                      className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                      disabled={submitting || !canUseNewsEmail}
                      maxLength={200}
                    />
                  </label>
                ) : null}

                <label
                  className={`mt-4 flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm shadow-sm ${
                    form.notificationType === "line" ? "opacity-60" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={form.includeAudio}
                    onChange={(event) => {
                      setForm((prev) => ({ ...prev, includeAudio: event.target.checked }));
                      setError(null);
                    }}
                    disabled={submitting || form.notificationType === "line"}
                    className="h-4 w-4 accent-[#A67C52]"
                  />
                  <span className="font-bold text-[#5D4D46]">音声ファイルを付ける</span>
                </label>
              </div>

              {error ? (
                <div className="mt-4 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
                  <p>{error.message}</p>
                  {error.detail ? <p className="mt-1 text-xs text-[#B86A5D]">{error.detail}</p> : null}
                  {error.newsGoogleReconsent ? (
                    <button
                      type="button"
                      onClick={startNewsGoogleReconsent}
                      disabled={submitting}
                      className="mt-3 inline-flex items-center justify-center rounded-full bg-[#B07A55] px-4 py-2 text-sm font-black text-white transition hover:bg-[#986648] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      連携をやり直す
                    </button>
                  ) : null}
                </div>
              ) : null}

            </>
          ) : null}

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || submitting}
              className="flex-1 rounded-[1.4rem] bg-[#5D4D46] px-4 py-3 text-sm font-black text-[#FDF8F5] shadow-sm transition-all hover:bg-[#4A3E39] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? "送信中..." : "依頼する"}
            </button>
            <button
              type="button"
              onClick={closePage}
              disabled={submitting}
              className="rounded-[1.4rem] border border-[#EADBC8] bg-white px-4 py-3 text-sm font-bold text-[#5D4D46] disabled:cursor-not-allowed disabled:opacity-60"
            >
              閉じる
            </button>
          </div>
        </div>
      </div>
      {result ? (
        <div className="pointer-events-none fixed bottom-6 left-1/2 z-30 w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 rounded-[1.4rem] border border-[#D8E5D2] bg-[#F4FAF1] px-4 py-3 text-sm text-[#3F5C3B] shadow-[0_18px_40px_rgba(93,77,70,0.18)]">
          <p className="font-black">{mode === "report" ? "レポート依頼を受け付けました。" : "ニュース依頼を受け付けました。"}</p>
          <p className="mt-1 leading-6">完了または失敗したらトーストでお知らせします。</p>
        </div>
      ) : null}
    </main>
  );
}
